/**
 * Owner: Masa Bayu
 * File: qx-draft-blocks.ts
 * Date:Time: 2026-10-16 06:32
 * Description: contentEditable DOM ↔ QXTextContent (marks · figure · note).
 */

import {
  htmlFromDraftFigure,
  parseDraftFigureFloat,
  type DraftFigureFloat,
  type DraftFigurePicture,
} from './qx-draft-image';
import {
  captionBlockFromFigure,
  captionPlainFromInlines,
} from './qx-draft-image-note';
import { draftFigurePictureFromDom } from './qx-draft-image-style';
import { inlinesFromElement, inlinesToHtml } from './qx-draft-marks';
import {
  createQXTextContent,
  createQXTextFigureBlock,
  createQXTextHeadingBlock,
  createQXTextInline,
  createQXTextListBlock,
  createQXTextListItemBlock,
  createQXTextParagraphBlock,
  type QXTextAlignment,
  type QXTextBlock,
  type QXTextContent,
  type QXTextListBlock,
  type QXTextParagraphFormat,
  type QXWDocument,
  createQXTextElement,
} from '@qx/qxw-core';

export const DRAFT_ELEMENT_ID = 'el_qxwriter_shell_draft';

export type DraftPreviewAsset = {
  readonly id: string;
  readonly previewSrc: string;
  readonly alt: string;
  readonly displayWidthPx?: number | undefined;
  readonly displayHeightPx?: number | undefined;
  readonly displayFloat?: DraftFigureFloat | undefined;
  /** Session picture style · ≠ tip schema invent */
  readonly displayPicture?: DraftFigurePicture | undefined;
};

export type DraftEditorSync = {
  readonly content: QXTextContent;
  readonly previewAssets: readonly DraftPreviewAsset[];
  /** Block indexes whose DOM source was `<ol>` · session hydrate only · ≠ tip schema */
  readonly orderedListBlockIndexes: readonly number[];
};

function alignmentFromElement(el: HTMLElement): QXTextAlignment | undefined {
  const raw = (el.style.textAlign || el.getAttribute('align') || '').toLowerCase();
  if (raw === 'left' || raw === 'center' || raw === 'right' || raw === 'justify') {
    return raw;
  }
  return undefined;
}

/** Parse length → pt. allowZero keeps explicit 0. */
function styleLengthPt(raw: string, allowZero: boolean): number | undefined {
  const value = raw.trim().toLowerCase();
  if (!value) return undefined;
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  if (n === 0) return allowZero ? 0 : undefined;
  if (value.endsWith('pt')) return Math.round(n);
  if (value.endsWith('px')) return Math.round(n * 0.75);
  if (value.endsWith('em')) return Math.round(n * 12);
  return Math.round(n);
}

function paragraphFormat(el: HTMLElement): QXTextParagraphFormat | undefined {
  const alignment = alignmentFromElement(el);
  const firstLineIndent = styleLengthPt(el.style.textIndent || '', false);
  // Document Gap uses data-qxw-gap-top margin — ≠ author Space Before.
  const spacingBefore =
    el.dataset.qxwGapTop === '1'
      ? undefined
      : styleLengthPt(el.style.marginTop || '', true);
  // Legacy Gap carriers still read as spacingAfter.
  const spacingAfter = styleLengthPt(
    el.style.paddingBottom || el.style.marginBottom || '',
    true,
  );
  if (
    alignment === undefined &&
    firstLineIndent === undefined &&
    spacingBefore === undefined &&
    spacingAfter === undefined
  ) {
    return undefined;
  }
  return {
    ...(alignment !== undefined ? { alignment } : {}),
    ...(firstLineIndent !== undefined ? { firstLineIndent } : {}),
    ...(spacingBefore !== undefined ? { spacingBefore } : {}),
    ...(spacingAfter !== undefined ? { spacingAfter } : {}),
  };
}

function paragraphStyleAttr(format: QXTextParagraphFormat | undefined): string {
  if (!format) return '';
  const parts: string[] = [];
  if (format.alignment) parts.push(`text-align:${format.alignment}`);
  if (typeof format.firstLineIndent === 'number' && format.firstLineIndent > 0) {
    parts.push(`text-indent:${format.firstLineIndent}pt`);
  }
  if (typeof format.spacingBefore === 'number' && format.spacingBefore > 0) {
    parts.push(`margin-top:${format.spacingBefore}pt`);
  }
  if (typeof format.spacingAfter === 'number' && format.spacingAfter > 0) {
    parts.push(`padding-bottom:${format.spacingAfter}pt`);
  }
  return parts.length > 0 ? ` style="${parts.join(';')}"` : '';
}

function listFromElement(listEl: HTMLElement): QXTextListBlock {
  const items = Array.from(listEl.children)
    .filter((child): child is HTMLElement => child instanceof HTMLElement && child.tagName === 'LI')
    .map((li) => {
      const nested = Array.from(li.children).find(
        (c): c is HTMLElement =>
          c instanceof HTMLElement && (c.tagName === 'UL' || c.tagName === 'OL'),
      );
      const clone = li.cloneNode(true) as HTMLElement;
      for (const nest of Array.from(clone.querySelectorAll('ul, ol'))) {
        nest.remove();
      }
      const inlines = inlinesFromElement(clone);
      return nested
        ? createQXTextListItemBlock(inlines, listFromElement(nested))
        : createQXTextListItemBlock(inlines);
    });
  return createQXTextListBlock(items.length > 0 ? items : [createQXTextListItemBlock()]);
}

function nextAssetId(counter: { n: number }, existing: string | null): string {
  if (existing && existing.trim().length > 0) return existing.trim();
  counter.n += 1;
  return `asset_draft_${counter.n}`;
}

function readImgPx(img: HTMLImageElement, attr: 'data-qx-width' | 'data-qx-height'): number | undefined {
  const raw = img.getAttribute(attr) || (attr === 'data-qx-width' ? img.style.width : img.style.height);
  if (!raw || raw === 'auto') return undefined;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

function figureFromImg(
  img: HTMLImageElement,
  counter: { n: number },
  previewAssets: DraftPreviewAsset[],
): QXTextBlock {
  const id = nextAssetId(counter, img.getAttribute('data-qx-asset-id'));
  const alt = img.getAttribute('alt') ?? '';
  const src = img.getAttribute('src') ?? '';
  const displayWidthPx = readImgPx(img, 'data-qx-width');
  const displayHeightPx = readImgPx(img, 'data-qx-height');
  const figureEl = img.closest('figure');
  const displayFloat =
    parseDraftFigureFloat(figureEl?.getAttribute('data-qx-float')) ?? undefined;
  const displayPicture =
    figureEl instanceof HTMLElement ? draftFigurePictureFromDom(figureEl) : undefined;
  img.setAttribute('data-qx-asset-id', id);
  if (src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('http')) {
    previewAssets.push({
      id,
      previewSrc: src,
      alt,
      ...(displayWidthPx !== undefined ? { displayWidthPx } : {}),
      ...(displayHeightPx !== undefined ? { displayHeightPx } : {}),
      ...(displayFloat !== undefined ? { displayFloat } : {}),
      ...(displayPicture ? { displayPicture } : {}),
    });
  }
  const caption = captionBlockFromFigure(figureEl);
  return createQXTextFigureBlock({
    assetRef: { id },
    alt: alt.length > 0 ? alt : 'Image',
    ...(caption ? { caption } : {}),
  });
}

function isImageOnlyContainer(el: HTMLElement): HTMLImageElement | null {
  const imgs = Array.from(el.querySelectorAll('img'));
  if (imgs.length !== 1) return null;
  const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  return text.length === 0 ? imgs[0]! : null;
}

function blockFromElement(
  el: HTMLElement,
  counter: { n: number },
  previewAssets: DraftPreviewAsset[],
): { block: QXTextBlock; orderedList?: boolean } | null {
  const tag = el.tagName.toLowerCase();
  if (tag === 'img') {
    return { block: figureFromImg(el as HTMLImageElement, counter, previewAssets) };
  }
  if (tag === 'figure') {
    const img = el.querySelector('img');
    if (img) return { block: figureFromImg(img, counter, previewAssets) };
  }
  if (tag === 'h1') return { block: createQXTextHeadingBlock(1, inlinesFromElement(el)) };
  if (tag === 'h2') return { block: createQXTextHeadingBlock(2, inlinesFromElement(el)) };
  if (tag === 'h3') return { block: createQXTextHeadingBlock(3, inlinesFromElement(el)) };
  if (tag === 'ul') return { block: listFromElement(el) };
  if (tag === 'ol') return { block: listFromElement(el), orderedList: true };
  if (tag === 'blockquote') {
    return { block: { type: 'quote', inlines: inlinesFromElement(el) } };
  }
  if (tag === 'pre') {
    return { block: { type: 'code', inlines: inlinesFromElement(el) } };
  }
  if (tag === 'p' || tag === 'div') {
    const onlyImg = isImageOnlyContainer(el);
    if (onlyImg) return { block: figureFromImg(onlyImg, counter, previewAssets) };
    return { block: createQXTextParagraphBlock(inlinesFromElement(el), paragraphFormat(el)) };
  }
  return null;
}

function appendMappedBlock(
  mapped: { block: QXTextBlock; orderedList?: boolean },
  blocks: QXTextBlock[],
  orderedListBlockIndexes: number[],
): void {
  if (mapped.orderedList) orderedListBlockIndexes.push(blocks.length);
  blocks.push(mapped.block);
}

function walkDraftNodes(
  nodes: Iterable<ChildNode>,
  blocks: QXTextBlock[],
  previewAssets: DraftPreviewAsset[],
  orderedListBlockIndexes: number[],
  counter: { n: number },
): void {
  for (const child of Array.from(nodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = (child.textContent ?? '').replace(/\u00a0/g, ' ');
      if (text.trim().length === 0) continue;
      blocks.push(createQXTextParagraphBlock([createQXTextInline(text)]));
      continue;
    }
    if (!(child instanceof HTMLElement)) continue;
    // Word footer page number · presentation only · not tip text.
    if (child.classList.contains('qxw-spine-page__pagenum')) continue;
    // Template spine sheets · flatten so tip stays heading/paragraph blocks.
    if (
      child.classList.contains('qxw-spine-page') ||
      child.getAttribute('data-qxw-spine-kind')
    ) {
      walkDraftNodes(child.childNodes, blocks, previewAssets, orderedListBlockIndexes, counter);
      continue;
    }
    const mapped = blockFromElement(child, counter, previewAssets);
    if (!mapped) continue;
    appendMappedBlock(mapped, blocks, orderedListBlockIndexes);
  }
}

/** Map draft editor DOM → blocks + session Preview asset map. */
export function draftSyncFromEditor(root: HTMLElement): DraftEditorSync {
  const blocks: QXTextBlock[] = [];
  const previewAssets: DraftPreviewAsset[] = [];
  const orderedListBlockIndexes: number[] = [];
  const counter = { n: 0 };
  walkDraftNodes(root.childNodes, blocks, previewAssets, orderedListBlockIndexes, counter);
  return {
    content: blocks.length === 0 ? createQXTextContent('') : { blocks },
    previewAssets,
    orderedListBlockIndexes,
  };
}

/** Map draft editor DOM → native QXTextContent.blocks. */
export function draftContentFromEditor(root: HTMLElement): QXTextContent {
  return draftSyncFromEditor(root).content;
}

function listToHtml(list: QXTextListBlock, ordered: boolean): string {
  const tag = ordered ? 'ol' : 'ul';
  const items = list.items
    .map((item) => {
      const nested = item.list ? listToHtml(item.list, false) : '';
      return `<li>${inlinesToHtml(item.inlines)}${nested}</li>`;
    })
    .join('');
  return `<${tag}>${items}</${tag}>`;
}

/** Round-trip HTML for draft hydrate (marks · headings · lists · figures). */
export function htmlFromDraftContent(
  content: QXTextContent,
  previewByAssetId: Readonly<Record<string, string>> = {},
  orderedListBlockIndexes: ReadonlySet<number> | readonly number[] = [],
  previewWidthByAssetId: Readonly<Record<string, number>> = {},
  previewFloatByAssetId: Readonly<Record<string, DraftFigureFloat>> = {},
  previewHeightByAssetId: Readonly<Record<string, number>> = {},
  previewPictureByAssetId: Readonly<Record<string, DraftFigurePicture>> = {},
): string {
  const ordered =
    orderedListBlockIndexes instanceof Set
      ? orderedListBlockIndexes
      : new Set(orderedListBlockIndexes ?? []);
  if (content.blocks.length === 0) return '<p><br></p>';
  return content.blocks
    .map((block, blockIndex) => {
      switch (block.type) {
        case 'heading': {
          const tag = block.level <= 1 ? 'h1' : block.level === 2 ? 'h2' : 'h3';
          return `<${tag} id="outline_b${blockIndex}">${inlinesToHtml(block.inlines) || '<br>'}</${tag}>`;
        }
        case 'paragraph':
          return `<p${paragraphStyleAttr(block.format)}>${inlinesToHtml(block.inlines) || '<br>'}</p>`;
        case 'list':
          return listToHtml(block, ordered.has(blockIndex));
        case 'quote':
          return `<blockquote>${inlinesToHtml(block.inlines) || '<br>'}</blockquote>`;
        case 'code':
          return `<pre>${inlinesToHtml(block.inlines) || '<br>'}</pre>`;
        case 'figure': {
          const id = block.assetRef?.id ?? '';
          const note = block.caption
            ? captionPlainFromInlines(block.caption.inlines)
            : undefined;
          return htmlFromDraftFigure({
            assetId: id,
            alt: block.alt ?? 'Image',
            ...(id && previewByAssetId[id] ? { src: previewByAssetId[id] } : {}),
            ...(id && previewWidthByAssetId[id] ? { widthPx: previewWidthByAssetId[id] } : {}),
            ...(id && previewHeightByAssetId[id] ? { heightPx: previewHeightByAssetId[id] } : {}),
            ...(id && previewFloatByAssetId[id] ? { float: previewFloatByAssetId[id] } : {}),
            ...(id && previewPictureByAssetId[id] ? { picture: previewPictureByAssetId[id] } : {}),
            ...(note ? { note } : {}),
          });
        }
        default:
          return '';
      }
    })
    .filter(Boolean)
    .join('') || '<p><br></p>';
}

export function withDraftContent(
  doc: QXWDocument,
  content: QXTextContent,
  assetIds: readonly string[] = [],
): QXWDocument {
  const draft = {
    ...createQXTextElement({ id: DRAFT_ELEMENT_ID, name: 'Draft', text: '' }),
    content,
  };
  const existing = new Set(doc.assets.map((a) => a.id));
  const mergedAssets = [
    ...doc.assets,
    ...assetIds.filter((id) => !existing.has(id)).map((id) => ({ id })),
  ];
  return {
    ...doc,
    assets: mergedAssets,
    elements: [draft, ...doc.elements.filter((element) => element.id !== DRAFT_ELEMENT_ID)],
    metadata: { ...doc.metadata, updatedAt: new Date().toISOString() },
  };
}

export function readDraftContent(doc: QXWDocument): QXTextContent | null {
  const draft = doc.elements.find(
    (element) => element.id === DRAFT_ELEMENT_ID && element.type === 'text',
  );
  if (draft?.type === 'text') return draft.content;
  const first = doc.elements.find((element) => element.type === 'text');
  return first?.type === 'text' ? first.content : null;
}
